import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Star, CheckCircle2, Sparkles, Send, ExternalLink, ShieldCheck,
  Upload, ArrowLeft, Camera, ThumbsUp, Heart
} from "lucide-react";
import { toast } from "sonner";

export default function ReviewSubmission() {
  const { slug } = useParams();
  const businessName = slug
    ? slug.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
    : "Colibri Media Studio";

  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [authorName, setAuthorName] = useState("");
  const [authorEmail, setAuthorEmail] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!authorName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast.success("Thank you for your valuable feedback!");
    }, 600);
  };

  return (
    <div
      className="min-h-screen flex flex-col justify-between py-10 px-4 sm:px-6 font-sans select-none"
      style={{
        background: "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 50%, #CBD5E1 100%)",
      }}
      data-testid="review-submission-page"
    >
      {/* ── Top Header Bar ── */}
      <header className="max-w-xl mx-auto w-full flex items-center justify-between pb-6">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
            <Sparkles className="w-4 h-4" />
          </div>
          <span className="font-bold text-base text-slate-800 tracking-tight">
            LaunchDeck Verified Reviews
          </span>
        </Link>

        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100/80 px-2.5 py-1 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Verified Client Flow</span>
        </div>
      </header>

      {/* ── Main Review Card ── */}
      <main className="max-w-xl mx-auto w-full">
        <Card className="p-6 sm:p-8 bg-white shadow-xl rounded-2xl border border-slate-200/80 space-y-6">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Info Header */}
              <div className="text-center space-y-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center mx-auto shadow-lg font-black text-xl">
                  {businessName.slice(0, 2).toUpperCase()}
                </div>
                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  {businessName}
                </h1>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  How was your experience working with our team? Your feedback guides our continued excellence.
                </p>
              </div>

              {/* 5-Star Rating Selector */}
              <div className="flex flex-col items-center justify-center gap-2 py-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Select Your Overall Rating
                </span>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const active = (hoverRating || rating) >= star;
                    return (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(star)}
                        className="p-1 hover:scale-115 transition-transform cursor-pointer"
                      >
                        <Star
                          className={`w-9 h-9 ${
                            active
                              ? "text-amber-400 fill-amber-400 drop-shadow-xs"
                              : "text-slate-300"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                <span className="text-sm font-bold text-slate-700">
                  {rating === 5 && "⭐⭐⭐⭐⭐ Outstanding Experience"}
                  {rating === 4 && "⭐⭐⭐⭐ Very Good"}
                  {rating === 3 && "⭐⭐⭐ Average / Room for Improvement"}
                  {rating <= 2 && "⭐⭐ Needs Attention"}
                </span>
              </div>

              {/* High Rating vs Constructive Feedback Flow */}
              {rating >= 4 ? (
                <div className="p-4 rounded-xl bg-blue-50/70 border border-blue-200 text-blue-900 space-y-3">
                  <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-blue-700">
                    <ThumbsUp className="w-4 h-4" />
                    <span>Share Directly on Public Directory</span>
                  </div>
                  <p className="text-xs text-blue-800">
                    Reviews left on Google and Yelp significantly help local business growth. Feel free to copy your review to our official page:
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <a
                      href="https://search.google.com/local/writereview"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-slate-800 text-xs font-bold hover:shadow-xs transition-shadow"
                    >
                      <span>Google Business Profile</span>
                      <ExternalLink className="w-3 h-3 text-blue-600" />
                    </a>
                    <a
                      href="https://www.yelp.com"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-slate-800 text-xs font-bold hover:shadow-xs transition-shadow"
                    >
                      <span>Yelp for Business</span>
                      <ExternalLink className="w-3 h-3 text-red-600" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50/80 border border-amber-200 text-amber-900 space-y-1.5">
                  <div className="font-bold text-xs uppercase tracking-wider text-amber-800">
                    Direct Leadership Review
                  </div>
                  <p className="text-xs text-amber-800">
                    Your feedback will be delivered confidentially directly to our executive managing partners so we can resolve any outstanding concerns immediately.
                  </p>
                </div>
              )}

              {/* Form Inputs */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Your Name *
                    </label>
                    <Input
                      placeholder="e.g. Sarah Connor"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      required
                      className="h-10 text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Email Address (Optional)
                    </label>
                    <Input
                      type="email"
                      placeholder="e.g. sarah@example.com"
                      value={authorEmail}
                      onChange={(e) => setAuthorEmail(e.target.value)}
                      className="h-10 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-700 block">
                      Review Details &amp; Experience
                    </label>
                    <div className="flex items-center gap-1 text-[11px] text-slate-400">
                      <span>Click to add:</span>
                      {["Stunning Edits", "Fast Turnaround", "Professional Crew"].map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => setReviewText(prev => prev ? `${prev} ${tag}!` : `${tag}!`)}
                          className="px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors cursor-pointer"
                        >
                          +{tag}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    placeholder="Tell us what you liked most about our service, responsiveness, or production quality..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    rows={4}
                    className="text-sm resize-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md gap-2"
              >
                {submitting ? "Submitting Review..." : "Submit Review & Feedback →"}
              </Button>
            </form>
          ) : (
            /* Thank You Success Screen */
            <div className="py-10 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-md">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                Thank You for Your Feedback!
              </h2>
              <p className="text-sm text-slate-600 max-w-sm mx-auto">
                Your review has been successfully submitted and verified. We truly value your partnership with {businessName}.
              </p>
              <div className="pt-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Return to Home
                </Link>
              </div>
            </div>
          )}
        </Card>
      </main>

      {/* ── Footer ── */}
      <footer className="max-w-xl mx-auto w-full text-center text-xs text-slate-400 pt-6">
        Powered by LaunchDeck Customer Experience Engine · 256-Bit SSL Encrypted
      </footer>
    </div>
  );
}
